import { XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { NoticeKind, NoticeTemplatePreview as PreviewResult, previewNoticeTemplate } from 'services'
import { Button } from 'stories/components'

/**
 * What a notice level's template produces, rendered against a real loan before a run mails it to
 * every borrower on that level. The server picks a loan on the level unless one is named here.
 * Nothing is attached or emailed.
 */
export default function NoticeTemplatePreview({
  kind,
  level,
  levelLabel,
  template,
  onClose,
}: {
  kind: NoticeKind
  level: string
  levelLabel: string
  template: string
  onClose: () => void
}) {
  const [loanInput, setLoanInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PreviewResult | null>(null)
  const [url, setUrl] = useState('')

  const load = (loanNumber?: number) => {
    setLoading(true)
    previewNoticeTemplate(kind, { level, template, ...(loanNumber ? { loanNumber } : {}) })
      .then((res) => setResult(res))
      .catch((e: any) => setResult({ success: 0, message: e?.message || 'Could not render the preview.' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  // The PDF is shown from a blob URL, released when it is replaced or the preview closes.
  useEffect(() => {
    if (!result?.pdf) {
      setUrl('')
      return
    }
    const bytes = Uint8Array.from(atob(result.pdf), (c) => c.charCodeAt(0))
    const next = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [result])

  const onPreviewLoan = () => {
    const n = Number(loanInput.trim())
    if (n > 0) load(n)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[90vh] w-[860px] max-w-[95vw] flex-col rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-1 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">Preview: {levelLabel}</h2>
            <p className="text-sm text-gray-500">{template}</p>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-gray-600">
            {loading
              ? 'Rendering…'
              : result?.success
              ? `Rendered with loan ${result.loanNo}${result.borrower ? ` (${result.borrower})` : ''}${
                  result.language ? `, ${result.language} template` : ''
                }`
              : ''}
          </span>
          <div className="flex items-center gap-2">
            <input
              className="w-36 rounded border border-gray-300 px-2 py-1 text-sm"
              placeholder="Loan number"
              value={loanInput}
              onChange={(e) => setLoanInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onPreviewLoan()}
            />
            <Button color="gray" outline onClick={onPreviewLoan} disabled={loading || !Number(loanInput.trim())}>
              Preview this loan
            </Button>
          </div>
        </div>

        {!loading && result?.success === 1 && result.inWindow === false && (
          <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            This loan is outside the notice window, so the notice&apos;s own figures print blank here. A run only sends
            this notice to loans inside the window.
          </div>
        )}

        <div className="flex-1 overflow-hidden rounded border border-slate-200 bg-slate-50">
          {loading ? (
            <p className="py-16 text-center text-sm text-gray-400">Rendering the notice…</p>
          ) : url ? (
            <iframe title="Notice preview" src={url} className="h-full w-full" />
          ) : (
            <p className="py-16 text-center text-sm text-gray-500">{result?.message || 'Nothing to preview.'}</p>
          )}
        </div>
      </div>
    </div>
  )
}
